'use client';

import clsx from 'clsx';
import { type ReactNode, useCallback, useState } from 'react';

import Flex from '@commons/containers/Flex';
import { breakpoints, useMediaQueries } from '@commons/contexts/responsive';
import { Link } from '@commons/i18n/navigation';
import type { DrupalImage as DrupalImageType } from '@commons/types/drupal';
import type { Theme } from '@commons/types/styles';

import { Button } from '../Button';
import DrupalImage from '../Image/Drupal';
import { MotionDiv } from '../Motion';
import Separator, { type SeparatorProps } from '../Separator';
import Tag from '../Tag';
import Text from '../Text';
import styles from './FeaturedTile.module.scss';

/**
 * Next.js Image `sizes` property for the FeaturedTile component.
 *
 * Featured tiles are mostly used in a 2-column layout, so we can use the same sizes property.
 *
 * @see https://nextjs.org/docs/app/api-reference/components/image#sizes
 */

export const FEATURED_TILE_IMAGE_SIZES = `(max-width: ${breakpoints.md}px) 100vw, (max-width: ${breakpoints.xl}px) 640px, 880px`;

/**
 * Whether the FeaturedTile content should be split between the top and bottom, or aligned at the end.
 */

export type ContentAlignment = 'end' | 'space-between';

// Framer motion animation variants.

const detailsVariantsEnd = {
  initial: { opacity: 0, y: 100 },
  hover: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 100 },
};

const descriptionVariants = {
  initial: { opacity: 0, y: -20 }, // Initially hidden and above position.
  hover: { opacity: 1, y: 0 }, // Fade in and slide down.
};

const buttonVariants = {
  initial: { opacity: 0, y: 20 }, // Initially hidden and below position.
  hover: { opacity: 1, y: 0 }, // Fade in and slide up.
};

const buttonVariantsMobile = {
  initial: { opacity: 1, y: 0 }, // Always visible on mobile.
  hover: { opacity: 1, y: 0 },
};

const getWrapperVariants = (translateY: number) => ({
  initial: { y: translateY }, // Wrapper starts at the bottom.
  hover: { y: 0 }, // Wrapper moves to its final position.
  exit: { y: translateY, transition: { delay: 1, duration: 0.5, ease: 'easeInOut' } },
});

// Type definition for the FeaturedTile component props.

type FeaturedTileProps = {
  title: string;
  href?: string;
  tag?: string;
  dash?: boolean | SeparatorProps;
  logo?: DrupalImageType;
  image?: ReactNode;
  description?: string;
  buttonText?: string;
  theme?: Theme;
  roundCorners?: boolean;
  justify?: ContentAlignment;
  aspectRatio?: 'square' | 'portrait';
  disableScroll?: boolean;
  className?: string;
};

/**
 * Featured Tile component.
 */

const FeaturedTile = (props: FeaturedTileProps) => {
  const { ready, minWidth } = useMediaQueries();
  const [titleHeight, setTitleHeight] = useState(0);
  const [contentHeight, setContentHeight] = useState(0);

  const {
    title,
    href,
    tag,
    dash = false,
    logo,
    image,
    description,
    buttonText,
    theme = 'black',
    roundCorners = false,
    justify = 'end',
    aspectRatio = 'square',
    disableScroll = false,
    className,
    ...rest
  } = props;

  const isMobile = !ready || !minWidth.md;

  // Measure and set the title height on mount.
  const titleRef = useCallback((node: HTMLHeadingElement | null) => {
    if (node) setTitleHeight(node.offsetHeight);
  }, []);

  // Measure and set the content height on mount.
  const contentRef = useCallback((node: HTMLDivElement | null) => {
    if (node) setContentHeight(node.offsetHeight);
  }, []);

  const translateY = contentHeight - titleHeight;
  const wrapperVariants = isMobile
    ? {}
    : justify === 'end'
      ? getWrapperVariants(translateY)
      : undefined;

  const hasDash = dash !== false;
  const dashProps = hasDash && typeof dash === 'object' ? dash : undefined;

  const renderedContent = (
    <MotionDiv
      initial="initial"
      whileHover={disableScroll ? undefined : 'hover'}
      animate={disableScroll ? 'hover' : 'initial'}
      className={styles.tileContainer}
    >
      {image && <div className={styles.imageWrapper}>{image}</div>}
      <MotionDiv
        ref={contentRef}
        variants={wrapperVariants}
        transition={{ duration: 0.5, ease: 'easeInOut' }}
        className={clsx(styles.content, justify === 'space-between' && styles.spaceBetweenContent)}
      >
        <Flex theme={theme} direction="column" className={styles.topContent}>
          <Flex.Item ref={titleRef}>
            {hasDash && <Separator dash color="primary" marginBottom="xs" {...dashProps} />}
            {tag && <Tag className={styles.tag}>{tag}</Tag>}
            <Text
              as="h3"
              level={disableScroll ? 'h2' : 'h4'}
              transform="uppercase"
              marginBottom="none"
              className={styles.title}
            >
              {title}
            </Text>
          </Flex.Item>
          {description && (
            <MotionDiv
              className={styles.description}
              variants={justify === 'end' ? detailsVariantsEnd : descriptionVariants}
              transition={
                justify === 'end'
                  ? { delay: 0.1, duration: 0.6, ease: 'easeInOut' }
                  : { duration: 0.3, ease: 'easeInOut' }
              }
            >
              <Text marginBottom="sm">{description}</Text>
            </MotionDiv>
          )}
        </Flex>
        {buttonText && (
          <Flex.Item theme={theme} className={styles.topContent}>
            {logo && <DrupalImage {...logo} className={styles.logo} />}
            <MotionDiv
              className={styles.button}
              variants={
                isMobile
                  ? buttonVariantsMobile
                  : justify === 'end'
                    ? detailsVariantsEnd
                    : buttonVariants
              }
              transition={
                justify === 'end'
                  ? { delay: 0.2, duration: 0.6, ease: 'easeInOut' }
                  : { duration: 0.3, ease: 'easeInOut' }
              }
            >
              <Button variant="primary">{buttonText}</Button>
            </MotionDiv>
          </Flex.Item>
        )}
      </MotionDiv>
    </MotionDiv>
  );

  const classes = clsx(
    styles.featuredTile,
    aspectRatio && styles[aspectRatio],
    roundCorners && styles.roundCorners,
    justify === 'space-between' && styles.spaceBetween,
    className
  );

  if (href) {
    return (
      <Link href={href} className={classes} {...rest}>
        {renderedContent}
      </Link>
    );
  }

  return (
    <div className={classes} {...rest}>
      {renderedContent}
    </div>
  );
};

export default FeaturedTile;
